import type { Organization } from "@/lib/api";

interface SidebarSessionUser {
	name?: string | null;
	email?: string | null;
	image?: string | null;
}

interface SidebarActiveOrg {
	id?: string | null;
	slug?: string | null;
	name?: string | null;
	logo?: string | null;
}

interface SidebarOrgDisplayState {
	displayName: string;
	displayLogo?: string;
	displayInitial: string;
	activeSlug: string | null;
}

export function resolveOrganizationDisplay(params: {
	isAuthenticated: boolean;
	urlOrgSlug: string | null;
	activeOrg?: SidebarActiveOrg | null;
	sessionUser?: SidebarSessionUser | null;
	organizations: Organization[];
}): SidebarOrgDisplayState {
	const matchedUrlOrg = params.urlOrgSlug
		? params.organizations.find((org) => org.slug === params.urlOrgSlug)
		: null;

	if (params.isAuthenticated) {
		if (params.urlOrgSlug) {
			const displayName = matchedUrlOrg?.name || params.urlOrgSlug;
			return {
				displayName,
				displayLogo: matchedUrlOrg?.logo || undefined,
				displayInitial: displayName[0] || "W",
				activeSlug: params.urlOrgSlug,
			};
		}

		const displayName =
			params.activeOrg?.name || params.sessionUser?.name || "Personal";
		return {
			displayName,
			displayLogo:
				params.activeOrg?.logo || params.sessionUser?.image || undefined,
			displayInitial:
				params.activeOrg?.name?.[0] ||
				params.sessionUser?.name?.[0] ||
				params.sessionUser?.email?.[0] ||
				"P",
			activeSlug: params.activeOrg?.slug || null,
		};
	}

	// When the URL pins a workspace, the sidebar must reflect it — never fall
	// through to organizations[0], which can show a stale/cached org name on a
	// different URL (e.g. visitor's last-viewed workspace bleeding through).
	const publicOrg = params.urlOrgSlug ? matchedUrlOrg : params.organizations[0];
	const displayName =
		publicOrg?.name || params.urlOrgSlug || "Select Organization";
	return {
		displayName,
		displayLogo: publicOrg?.logo || undefined,
		displayInitial: displayName[0] || "W",
		activeSlug: params.urlOrgSlug || publicOrg?.slug || null,
	};
}

export function isOrganizationActive(params: {
	organization: Organization;
	urlOrgSlug: string | null;
	activeOrg?: SidebarActiveOrg | null;
}): boolean {
	if (params.urlOrgSlug) {
		return params.organization.slug === params.urlOrgSlug;
	}

	return (
		params.organization.id === params.activeOrg?.id ||
		params.organization.slug === params.activeOrg?.slug
	);
}
